import html2pdf from "html2pdf.js";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { getEmpresaConfig, empresaBrandHtml } from "./empresaConfig";

dayjs.locale("es");

function esc(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const fmtMoney = (val) => {
  const n = parseFloat(String(val ?? 0));
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};

const fmtFecha = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY") : "—";
};

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #fff; color: #111; font-family: "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 10pt; }
  .page { width: 190mm; margin: 0 auto; padding: 14mm 16mm 18mm; background: #fff; }

  /* Header */
  .hdr { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px; border-bottom: 3px solid #f90336; margin-bottom: 4px; }
  .hdr-brand { font-size: 22pt; font-weight: 800; color: #111; letter-spacing: -0.5px; }
  .hdr-sub { font-size: 9pt; color: #555; margin-top: 2px; }
  .hdr-right { text-align: right; font-size: 8pt; color: #444; line-height: 1.7; }
  .hdr-title { font-size: 11pt; font-weight: 700; color: #111; }
  .rule { border: 0; border-top: 1px solid #ccc; margin: 4px 0 14px; }

  /* Section title */
  .section-title { font-size: 7pt; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #f90336; margin: 22px 0 10px; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0; }
  .section-title:first-of-type { margin-top: 8px; }

  /* KPI grid */
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 4px; }
  .kpi-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; }
  .kpi-label { font-size: 6.5pt; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
  .kpi-value { font-size: 16pt; font-weight: 800; line-height: 1; margin-bottom: 4px; }
  .kpi-sub { font-size: 7pt; color: #94a3b8; }
  .kpi-green  { color: #15803d; }
  .kpi-red    { color: #b91c1c; }
  .kpi-indigo { color: #4338ca; }
  .kpi-rose   { color: #9f1239; }

  /* Tables */
  table { width: 100%; border-collapse: collapse; font-size: 7.5pt; margin-bottom: 4px; }
  thead tr { background: #f90336; color: #fff; }
  thead th { padding: 5px 7px; text-align: left; font-weight: 600; font-size: 7pt; white-space: nowrap; }
  thead th.r { text-align: right; }
  tbody tr { border-bottom: 1px solid #e5e7eb; }
  tbody tr:nth-child(even) { background: #f9fafb; }
  tbody td { padding: 5px 7px; color: #111; vertical-align: middle; }
  tbody td.r { text-align: right; }
  tfoot tr { background: #f1f5f9; border-top: 2px solid #f90336; }
  tfoot td { padding: 5px 7px; font-weight: 700; font-size: 7.5pt; }
  tfoot td.r { text-align: right; }

  /* Badge */
  .badge { display: inline-block; padding: 1px 5px; border-radius: 4px; font-size: 6.5pt; font-weight: 700; }
  .badge-pending { background: #fef3c7; color: #92400e; }
  .badge-ok      { background: #dcfce7; color: #166534; }
  .badge-vencido { background: #fee2e2; color: #991b1b; }

  /* Two-column layout */
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .two-col table { font-size: 7pt; }

  /* Bar chart rows */
  .bar-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; font-size: 7.5pt; }
  .bar-label { width: 130px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-shrink: 0; }
  .bar-track { flex: 1; height: 10px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 4px; }
  .bar-val { width: 36px; text-align: right; font-weight: 600; color: #374151; flex-shrink: 0; }

  @media print {
    html, body { background: #fff !important; }
    .page { margin: 0; width: auto; padding: 14mm 16mm 18mm; }
    thead tr { background: #f90336 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tbody tr:nth-child(even) { background: #f9fafb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tfoot tr { background: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .bar-fill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .kpi-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; }
  }
`;

function barRows(items, labelKey, valueKey, color, { money = false, maxVal } = {}) {
  if (!items?.length) return "<p style='font-size:7.5pt;color:#888;margin:8px 0'>Sin datos</p>";
  const max = maxVal || Math.max(...items.map((i) => i[valueKey] || 0)) || 1;
  return items.map((item) => {
    const val = item[valueKey] || 0;
    const pct = Math.round((val / max) * 100);
    const valStr = money ? esc(fmtMoney(val)) : `${val}`;
    return `<div class="bar-row">
      <div class="bar-label">${esc(item[labelKey])}</div>
      <div class="bar-track"><div class="bar-fill" style="width:${pct}%;background:${color}"></div></div>
      <div class="bar-val">${valStr}</div>
    </div>`;
  }).join("");
}

const TIPO_LABELS = { producto: "Productos", servicio: "Servicios" };

function buildHtml({ data, periodoDescripcion, empresa }) {
  if (!data) return "";
  const createdAt = dayjs().format("D [de] MMMM [de] YYYY [a las] HH:mm");

  const ventas = data.ventas_del_periodo || [];
  const gastos = data.gastos_del_periodo || [];
  const totalVentas = ventas.reduce((s, v) => s + (v.total || 0), 0);
  const totalGastos = gastos.reduce((s, g) => s + (g.monto || 0), 0);

  const ventasPorDia = data.serie || [];
  const maxVentaDia = Math.max(...ventasPorDia.map((d) => d.ventas || 0), 1);
  const k = data.kpis || {};
  const tipos = (data.ventas?.por_tipo || []).map((t) => ({ ...t, label: TIPO_LABELS[t.nombre] || t.nombre }));

  return `<!doctype html>
<html lang="es">
<head><meta charset="utf-8"/><title>Reporte de Estadísticas</title>
<style>${css}</style></head>
<body><div class="page">

  <div class="hdr">
    ${empresaBrandHtml(empresa)}
    <div class="hdr-right">
      <div class="hdr-title">Reporte de Estadísticas</div>
      <div>Período: ${esc(periodoDescripcion || "—")}</div>
      <div>Generado: ${esc(createdAt)}</div>
    </div>
  </div>
  <hr class="rule"/>

  <!-- KPIs -->
  <div class="section-title">Resumen del período</div>
  <div class="kpi-grid">
    <div class="kpi-box">
      <div class="kpi-label">Ventas totales</div>
      <div class="kpi-value kpi-green">${esc(fmtMoney(k.ventas))}</div>
      <div class="kpi-sub">${k.tickets ?? 0} tickets en el período</div>
    </div>
    <div class="kpi-box">
      <div class="kpi-label">Ticket promedio</div>
      <div class="kpi-value kpi-indigo">${esc(fmtMoney(k.ticket_promedio))}</div>
      <div class="kpi-sub">Promedio por venta</div>
    </div>
    <div class="kpi-box">
      <div class="kpi-label">Gastos</div>
      <div class="kpi-value kpi-rose">${esc(fmtMoney(k.gastos))}</div>
      <div class="kpi-sub">Total registrado en el período</div>
    </div>
    <div class="kpi-box">
      <div class="kpi-label">Utilidad neta</div>
      <div class="kpi-value ${(k.utilidad_neta ?? 0) >= 0 ? "kpi-green" : "kpi-red"}">${esc(fmtMoney(k.utilidad_neta))}</div>
      <div class="kpi-sub">Ventas menos gastos</div>
    </div>
  </div>

  <!-- Ventas por día -->
  <div class="section-title">Ventas por día</div>
  ${ventasPorDia.length > 0 ? ventasPorDia.map((d) => {
    const pct = Math.round(((d.ventas || 0) / maxVentaDia) * 100);
    return `<div class="bar-row">
      <div class="bar-label">${esc(dayjs(d.fecha).format("D MMM"))}</div>
      <div class="bar-track"><div class="bar-fill" style="width:${pct}%;background:#f90336"></div></div>
      <div class="bar-val" style="width:70px">${esc(fmtMoney(d.ventas))}</div>
    </div>`;
  }).join("") : "<p style='font-size:7.5pt;color:#888;margin:8px 0'>Sin ventas en el período</p>"}

  <!-- Productos y formas de pago -->
  <div class="two-col" style="margin-top:14px">
    <div>
      <div class="section-title" style="margin-top:0">Lo que más se vende</div>
      ${barRows(data.ventas?.top_items, "nombre", "cantidad", "#6366f1")}
    </div>
    <div>
      <div class="section-title" style="margin-top:0">Ventas por forma de pago</div>
      ${barRows(data.ventas?.por_pago, "nombre", "total", "#10b981", { money: true })}
    </div>
  </div>

  <!-- Ventas por tipo -->
  <div class="section-title">Ventas por tipo</div>
  ${barRows(tipos, "label", "total", "#f90336", { money: true })}

  <!-- Gastos por concepto -->
  <div class="section-title">Gastos por concepto</div>
  ${barRows(data.gastos?.por_concepto, "nombre", "total", "#f43f5e", { money: true })}

  <!-- Gastos del período -->
  <div class="section-title">Gastos del período (${gastos.length})</div>
  ${gastos.length > 0 ? `
  <table>
    <thead><tr>
      <th style="width:46%">Descripción</th>
      <th style="width:30%">Fecha</th>
      <th class="r" style="width:24%">Monto</th>
    </tr></thead>
    <tbody>
      ${gastos.map((g) => `<tr>
          <td>${esc(g.descripcion)}</td>
          <td>${esc(fmtFecha(g.fecha))}</td>
          <td class="r" style="font-weight:700;color:#9f1239">${esc(fmtMoney(g.monto))}</td>
        </tr>`).join("")}
    </tbody>
    <tfoot><tr>
      <td colspan="2">Total gastos</td>
      <td class="r">${esc(fmtMoney(totalGastos))}</td>
    </tr></tfoot>
  </table>` : "<p style='font-size:7.5pt;color:#888;margin:6px 0'>Sin gastos registrados en este período</p>"}

  <!-- Ventas del período -->
  <div class="section-title">Ventas del período (${ventas.length})</div>
  ${ventas.length > 0 ? `
  <table>
    <thead><tr>
      <th style="width:12%">Folio</th>
      <th style="width:22%">Fecha</th>
      <th style="width:12%">Hora</th>
      <th class="r" style="width:18%">Productos</th>
      <th class="r" style="width:18%">Servicios</th>
      <th class="r" style="width:18%">Total</th>
    </tr></thead>
    <tbody>
      ${ventas.map((v) => `<tr>
          <td>#${esc(v.id_venta)}</td>
          <td>${esc(fmtFecha(v.fecha))}</td>
          <td>${esc(dayjs(v.fecha).format("HH:mm"))}</td>
          <td class="r">${esc(fmtMoney(v.total_producto))}</td>
          <td class="r">${esc(fmtMoney(v.total_servicio))}</td>
          <td class="r">${esc(fmtMoney(v.total))}</td>
        </tr>`).join("")}
    </tbody>
    <tfoot><tr>
      <td colspan="5">Total (${ventas.length} ventas)</td>
      <td class="r">${esc(fmtMoney(totalVentas))}</td>
    </tr></tfoot>
  </table>` : "<p style='font-size:7.5pt;color:#888;margin:6px 0'>Sin ventas en este período</p>"}

</div></body></html>`;
}

export async function previewEstadisticasReportPdf(payload) {
  const empresa = await getEmpresaConfig();
  return buildHtml({ ...payload, empresa });
}

export async function printEstadisticasReportPdf(payload) {
  const empresa = await getEmpresaConfig();
  const html = buildHtml({ ...payload, empresa });
  const element = document.createElement("div");
  element.innerHTML = html;
  html2pdf()
    .set({
      margin: 0,
      filename: "reporte_estadisticas.pdf",
      html2canvas: { scale: 2, backgroundColor: "#ffffff", useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(element)
    .save();
}
